import React, { useEffect, useState } from "react";
import BrandManagerShell from "./components/BrandManagerShell";
import apiService from "./services/api";
import "./brandManagerPages.css";

function unwrapData(payload) {
  return payload?.data ?? payload;
}

/**
 * Brand Settings — live-only.
 *
 * Only Remove Brand is implemented: POST /me/brands/leave removes the Brand
 * from this Client's Brand Manager portfolio. The platform Brand entity
 * remains intact.
 *
 * Preference controls from the mockup (Default View, Timezone, notifications,
 * display toggles) are omitted until a persistence contract exists.
 */
export default function BrandManagerSettings() {
  const [brands, setBrands] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(false);
  const [status, setStatus] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const mineRaw = await apiService.listMyBrands();
      let catalog = [];
      try {
        const catalogRaw = await apiService.listBrandCatalog();
        catalog = unwrapData(catalogRaw)?.brands || [];
      } catch {
        catalog = [];
      }
      const mine = unwrapData(mineRaw);
      const nameById = new Map(
        catalog
          .filter((b) => b && (b.id || b.brandId))
          .map((b) => [b.id || b.brandId, b.name || b.brandName || null]),
      );
      const memberships = Array.isArray(mine?.brandMemberships)
        ? mine.brandMemberships
        : [];
      const list = memberships
        .filter(
          (m) =>
            m && m.brandId && (m.status == null || m.status === "active"),
        )
        .map((m) => ({
          id: m.brandId,
          name:
            m.brandName || m.name || nameById.get(m.brandId) || m.brandId,
        }));
      setBrands(list);
      setSelectedId((prev) =>
        list.some((b) => b.id === prev) ? prev : list[0]?.id || "",
      );
      setStatus(null);
    } catch (err) {
      setBrands([]);
      setSelectedId("");
      setStatus({
        type: "error",
        text: err?.message || "Failed to load portfolio brands",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleRemove = async () => {
    const brand = brands.find((b) => b.id === selectedId);
    if (!brand || removing) return;
    const ok = window.confirm(
      `Remove "${brand.name}" from your Brand Manager?\n\nThis removes the Brand from your portfolio only. The platform Brand is not deleted. You can add it again later from Add New Brand.`,
    );
    if (!ok) return;
    setRemoving(true);
    setStatus({ type: "info", text: `Removing ${brand.name}…` });
    try {
      await apiService.leaveBrand(brand.id);
      setStatus({
        type: "ok",
        text: `${brand.name} was removed from your Brand Manager portfolio.`,
      });
      await refresh();
    } catch (err) {
      setStatus({
        type: "error",
        text: err?.message || `Failed to remove ${brand.name}`,
      });
    } finally {
      setRemoving(false);
    }
  };

  return (
    <BrandManagerShell>
      <div className="brand-manager-header">
        <h1>Brand Settings</h1>
        <p className="subtitle">
          Portfolio actions for Brands on your Brand Manager
        </p>
      </div>

      <div
        className="bm-page-panel"
        data-testid="brand-settings-page"
        data-settings-mode="live-only"
      >
        <p className="bm-page-lead">
          Only settings backed by live platform behavior are shown. Display
          preferences and notification controls will appear here when their
          persistence contract exists.
        </p>

        <div
          className="bm-settings-danger"
          data-testid="brand-settings-remove-zone"
        >
          <h2>Remove Brand from Brand Manager</h2>
          <p className="bm-page-lead" style={{ marginBottom: 0 }}>
            Removes the selected Brand from this Client&apos;s portfolio
            (canonical <code>POST /me/brands/leave</code>). Does not archive
            or delete the platform Brand.
          </p>

          <div className="bm-settings-row">
            <div>
              <label htmlFor="bm-settings-brand">Brand</label>
              <select
                id="bm-settings-brand"
                className="bm-select"
                value={selectedId}
                disabled={loading || brands.length === 0 || removing}
                onChange={(e) => setSelectedId(e.target.value)}
                data-testid="brand-settings-brand-select"
              >
                {brands.length === 0 ? (
                  <option value="">No brands in portfolio</option>
                ) : (
                  brands.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))
                )}
              </select>
            </div>
            <button
              type="button"
              className="bm-secondary-btn"
              disabled={!selectedId || removing || loading}
              onClick={handleRemove}
              data-testid="brand-settings-remove-btn"
            >
              {removing ? "Removing…" : "Remove Brand"}
            </button>
          </div>

          {status ? (
            <p
              className={`bm-inline-status${
                status.type === "error"
                  ? " bm-inline-status--error"
                  : status.type === "ok"
                    ? " bm-inline-status--ok"
                    : ""
              }`}
              data-testid="brand-settings-status"
              style={{ marginTop: 12 }}
            >
              {status.text}
            </p>
          ) : null}
        </div>
      </div>
    </BrandManagerShell>
  );
}
